/**
 * Starter-dashboard preflight (S21). The zero state asks "What do you want
 * to watch?" — Health / Storage / Activity, each mapped to a fixed set of S7
 * template categories. This module answers "which of that pack's templates
 * will actually work on THIS connection right now": it filters through the
 * S7 `availableTemplates` gate (version/capability-hidden templates never
 * even become candidates), then DRY-RUNS every remaining candidate through
 * the ONE tile fetch path (`trigger: 'preview'`, always read-only) — only
 * templates whose preview succeeds become tiles. Nothing is persisted here;
 * the caller (`components/StarterDashboards.tsx`) creates the dashboard and
 * adds the returned tiles.
 *
 * `runPreview` is injected so this stays unit-testable without an Electron
 * bridge (mirrors `refreshScheduler.ts`'s DI shape) — `runTemplatePreview`
 * below is the real implementation, wired at the call site.
 */
import type { DashboardEngineId, DashboardTile, TileLayout } from '../types/dashboardTypes';
import type { TableTile } from '../types/dashboardTypes';
import type { TileRunResult } from '../types/dashboardRuntime';
import type {
  DashboardTemplate,
  DashboardTemplateCapability,
  DashboardTemplateCategory,
} from '../templates/templateTypes';
import { availableTemplates } from '../templates/templateCatalog';
import { startTileExecution } from './tileExecutionService';
import { DEFAULT_TILE_SIZE } from '../utils/tileDefaultSize';
import { packTileLayouts } from '../utils/packTileLayouts';
import type { TileSuggestion } from '../utils/shapeSuggest';
import {
  buildTileFromSuggestion,
  pickSuggestionForProposal,
  shapeSuggestionsForProposal,
} from './aiProposalToTile';

export type StarterPack = 'health' | 'storage' | 'activity';

export const STARTER_PACK_LABELS: Record<StarterPack, string> = {
  health: 'Health',
  storage: 'Storage',
  activity: 'Activity',
};

/** Which S7 template categories each pack watches (Docs/Dashboards.md § Starter dashboards). */
export const STARTER_PACK_CATEGORIES: Record<StarterPack, DashboardTemplateCategory[]> = {
  health: ['performance', 'indexes'],
  storage: ['storage', 'backup'],
  activity: ['sessions', 'jobs'],
};

/** Per-connection dry-run concurrency cap — mirrors the scheduler's per-connection dispatch cap. */
export const STARTER_PREFLIGHT_MAX_CONCURRENT = 2;

export type StarterPreviewFn = (template: DashboardTemplate) => Promise<TileRunResult>;

export type StarterPreflightArgs = {
  pack: StarterPack;
  engine: DashboardEngineId;
  /** Live probe results — same shape `TemplatesPane` feeds `availableTemplates`. */
  serverVersion: string | undefined;
  capabilities: DashboardTemplateCapability[];
  connectionId: string;
  database?: string;
  runPreview: StarterPreviewFn;
  /** Test seam; defaults to `STARTER_PREFLIGHT_MAX_CONCURRENT`. */
  maxConcurrent?: number;
};

export type StarterPreflightResult = {
  /** Ready-to-add tiles, in the pack's candidate order, flow-packed layouts (`packTileLayouts`). */
  tiles: DashboardTile[];
  candidateCount: number;
  excludedCount: number;
};

/** Runs `fn` over `items` with at most `cap` in flight; results stay index-ordered. */
async function runWithCap<T, R>(
  items: T[],
  cap: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let cursor = 0;
  const worker = async () => {
    for (;;) {
      const index = cursor++;
      if (index >= items.length) return;
      results[index] = await fn(items[index]);
    }
  };
  const workerCount = Math.max(1, Math.min(cap, items.length));
  await Promise.all(Array.from({ length: workerCount }, worker));
  return results;
}

/**
 * Picks the autopilot suggestion for a passing preview — the SAME chain the
 * AI panel uses (`shapeSuggestionsForProposal` + the AI-type fallback in
 * `pickSuggestionForProposal`) so a template whose actual result shape can't
 * support its declared `defaultTileType` (e.g. a healthy-empty 0-row result)
 * degrades to a real, non-empty tile config instead of an invalid one —
 * never guessed, always derived from the preview's columns. Split from
 * `tileFromTemplate` because the suggestion decides the tile's SIZE, and all
 * sizes must be known before `packTileLayouts` can lay the batch out.
 */
function suggestionForTemplate(
  template: DashboardTemplate,
  result: TileRunResult & { ok: true },
): TileSuggestion {
  const picked = result.resultSets[0];
  const suggestions = shapeSuggestionsForProposal(picked?.columns ?? [], picked?.previewRows ?? []);
  return pickSuggestionForProposal(suggestions, template.defaultTileType);
}

/**
 * Builds the real tile from a passing preview's suggestion + packed layout.
 * Provenance is stamped to `{ kind: 'template', templateId }` afterward (the
 * suggestion builder only knows `'sql'`) so the tile's first-run subtitle
 * can resolve the template's `description` back via `findTemplateById`.
 */
function tileFromTemplate(
  template: DashboardTemplate,
  suggestion: TileSuggestion,
  layout: TileLayout,
  ctx: { connectionId: string; database?: string; engine: DashboardEngineId },
): DashboardTile {
  const built = buildTileFromSuggestion({
    proposal: {
      id: template.id,
      title: template.title,
      // proposal.tileType must fit the AI union (no 'map'); only title + sql
      // are consumed by the builder — the real type is the suggestion's.
      tileType: template.defaultTileType === 'map' ? 'table' : template.defaultTileType,
      sql: template.sql,
    },
    suggestion,
    connectionId: ctx.connectionId,
    database: ctx.database,
    engine: ctx.engine,
    layout,
  });
  const withSource: DashboardTile = {
    ...built,
    source: { kind: 'template', templateId: template.id, sql: template.sql },
  };
  return withSource.tileType === 'stat' && template.statValueColumn
    ? { ...withSource, valueColumn: template.statValueColumn }
    : withSource;
}

export async function starterPreflight(args: StarterPreflightArgs): Promise<StarterPreflightResult> {
  const categories = STARTER_PACK_CATEGORIES[args.pack];
  const candidates = availableTemplates(args.engine, args.serverVersion, args.capabilities, '').filter(
    (t) => categories.includes(t.category),
  );
  const cap = Math.max(1, args.maxConcurrent ?? STARTER_PREFLIGHT_MAX_CONCURRENT);
  const outcomes = await runWithCap(candidates, cap, async (template) => ({
    template,
    result: await args.runPreview(template),
  }));

  // Two passes: the autopilot suggestion decides each tile's TYPE (and so
  // its default size), then `packTileLayouts` flow-packs the whole batch —
  // charts/tables two per row, stats four per row (or slotted beneath an
  // earlier stat next to a taller tile) — before any tile is built.
  const passing = outcomes.flatMap(({ template, result }) =>
    // preview failed or was policy-blocked — excluded, never added
    result.ok ? [{ template, suggestion: suggestionForTemplate(template, result) }] : [],
  );
  const layouts = packTileLayouts(
    passing.map(({ suggestion }) => DEFAULT_TILE_SIZE[suggestion.tileType]),
  );
  const tiles = passing.map(({ template, suggestion }, index) =>
    tileFromTemplate(template, suggestion, layouts[index], {
      connectionId: args.connectionId,
      database: args.database,
      engine: args.engine,
    }),
  );

  return { tiles, candidateCount: candidates.length, excludedCount: candidates.length - tiles.length };
}

/** Real preview runner (S3's ONE fetch path) — the ephemeral tile mirrors `useTilePreview`'s. */
export function runTemplatePreview(
  template: DashboardTemplate,
  connectionId: string,
  database: string | undefined,
): Promise<TileRunResult> {
  const previewTile: TableTile = {
    id: `starter-preview-${template.id}`,
    tileType: 'table',
    title: 'Starter preview',
    layout: { x: 0, y: 0, w: 1, h: 1 },
    connectionId,
    database,
    source: { kind: 'sql', sql: template.sql },
    refresh: { mode: 'manual' },
    rowLimit: 1000,
    density: 'compact',
  };
  // Preview requests skip the repository tile lookup entirely (S6/S3
  // invariant) — no dashboard exists yet at preflight time, so the id is a
  // fixed, harmless placeholder never consulted by the policy.
  const { promise } = startTileExecution(previewTile, null, [], {
    trigger: 'preview',
    dashboardId: 'starter-preflight',
  });
  return promise;
}
